'use client';

import React, { useId, useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';
import { classnames } from '@/utils/classnames/classnames';
import { SeoContentProps } from './types';

// EWM-189 SEO content section.
//
// Sits below the product grid on category pages and renders editorial
// content authored on the commercetools Category custom field. The
// page H1 is the category title, so this section uses H2 and below;
// tailwind/typography `prose` classes handle the typographic scale.
//
// `html` is expected to be already-sanitised rich-text HTML produced
// by the BFF — sanitize-html runs again here as defence in depth.
// When the field is empty, the section renders nothing so categories
// without editorial content end at the grid + pagination.
const sanitizeOptions: sanitizeHtml.IOptions = {
  allowedTags: [...sanitizeHtml.defaults.allowedTags, 'img'],
  allowedAttributes: {
    ...sanitizeHtml.defaults.allowedAttributes,
    img: ['src', 'alt', 'width', 'height', 'loading'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
};

const SeoContent = ({ html, title, className }: SeoContentProps) => {
  const titleId = useId();
  const safeHtml = useMemo(() => (html ? sanitizeHtml(html, sanitizeOptions) : ''), [html]);

  if (!safeHtml.trim()) return null;

  return (
    <section
      aria-labelledby={title ? titleId : undefined}
      // Vertical padding is owned by the consuming band wrapper (the PLP
      // SEO band applies py-12 = Dan v0's 48px). Self-padding here stacked
      // on top of that → ~2× the mock's gap (EWM-318).
      className={classnames(className)}
      data-testid="seo-content"
    >
      {title && (
        <h2 id={titleId} className="text-primary text-20 lg:text-24 mb-4 font-bold">
          {title}
        </h2>
      )}
      <div
        className="prose prose-headings:text-primary prose-h2:text-24 prose-h3:text-18 prose-p:text-14 prose-li:text-14 max-w-none text-gray-700"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    </section>
  );
};

export default SeoContent;
